import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Button, FormField, Input, PasswordInput } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { getDefaultRouteForUser } from "@/lib/auth/defaultRoute";

export function LoginPage() {
  const { user, loading, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  if (!loading && user) {
    // Default landing. If the user
    // was redirected here from a protected page, send them back; else
    // route to "/", where the dashboard assembles the recipe for their
    // primary role.
    const fromPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    const redirect = fromPath && fromPath !== "/login"
      ? fromPath
      : getDefaultRouteForUser(user);
    return <Navigate to={redirect} replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
    } catch {
      /* error state handled by auth context */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black-pure text-white-pure flex items-center justify-center p-space-5">
      <div
        className="w-[440px] max-w-full bg-onyx border border-border-dark border-t-[2px] border-t-gold rounded-lg"
        style={{ padding: 40 }}
      >
        <img
          src="/assets/logo/phoenix-logo-horizontal-dark.jpeg"
          alt="Phoenix Capital Solutions"
          className="mx-auto block mb-space-3"
          style={{ height: 80 }}
        />
        <p className="text-center text-caption mb-space-5" style={{ color: "#9A9A9A" }}>
          Sign in to continue.
        </p>

        <form onSubmit={onSubmit} className="flex flex-col gap-space-3">
          <FormField label="Email" required htmlFor="login-email">
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="text-white-pure placeholder:text-gray-mid border-border-dark focus:border-gold"
              style={{ background: "#0F0F0F" }}
            />
          </FormField>
          <FormField label="Password" required htmlFor="login-password">
            <PasswordInput
              id="login-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="text-white-pure placeholder:text-gray-mid border-border-dark focus:border-gold"
              style={{ background: "#0F0F0F" }}
            />
          </FormField>

          {error && (
            <p className="text-label text-status-error" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" loading={submitting} className="w-full">
            Sign in
          </Button>
        </form>

        <p className="text-center mt-space-4" style={{ fontSize: 12, color: "#7A7A7A" }}>
          Proprietary &amp; Confidential · Phoenix Capital Solutions
        </p>
      </div>
    </div>
  );
}
